// Верификации судов — зеркало портала 73. ТОЛЬКО ЧТЕНИЕ.
//
// Инженеры заполняют верификации на портале 73 и продолжат это делать: здесь обзор для
// офиса — найти верификацию по судну/инженеру/статусу и прочитать чек-лист, не заходя на портал.
// Ни одной кнопки, которая что-то меняет, на этом экране нет и быть не должно.
//
// Один компонент на список и карточку: маршрутов два (/p73/verifications и .../:id), но экран
// один и тот же набор данных, а карточка без списка не бывает нужна.
import { Fragment, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { useRefs } from '../api/hooks'
import {
  useP73Refs, useВерификации, useВерификация,
} from '../api/p73'
import type { ВерификацияКарточка, ВерификацияСтрока, ФильтрыP73 } from '../api/p73'
import { EntityLink } from '../components/EntityLink'
import { FilterBar } from '../components/FilterBar'
import { PhotoGallery } from '../components/PhotoGallery'
import { PrintButton } from '../components/PrintButton'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtText } from '../lib/format'
import { листВерификации } from '../lib/print73'
import {
  ОТМЕТКИ, ПУТИ, РАЗМЕР_СТРАНИЦЫ, ТИПЫ_ВЕРИФИКАЦИИ, видСтатуса, значениеФормы,
  названиеСтатуса, процент, размерФайла,
} from '../lib/p73'

export default function Verifications() {
  const { id } = useParams<{ id: string }>()
  return id ? <Карточка id={id} /> : <Список />
}

// ═══════════════════════════════ список ═══════════════════════════════

function Список() {
  const [sp, setSp] = useSearchParams()
  // Судно и сделка живут в адресе: на этот экран будут вести ссылки из карточки судна
  // и из карточки сделки, и такую ссылку должно быть можно переслать.
  const vesselBx = sp.get('vessel_bx') || ''
  const dealBx = sp.get('deal_bx') || ''

  const [q, setQ] = useState('')
  const [status, setStatus] = useState('')
  const [type, setType] = useState('')
  const [engineer, setEngineer] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [initiative, setInitiative] = useState(false)
  const [start, setStart] = useState(0)

  const refs = useRefs()
  const p73 = useP73Refs()

  const фильтры = useMemo<ФильтрыP73>(() => ({
    q: q.trim() || undefined,
    status: status || undefined,
    type: type || undefined,
    engineer: engineer || undefined,
    vessel_bx: vesselBx || undefined,
    deal_bx: dealBx || undefined,
    from: from || undefined,
    to: to || undefined,
    initiative: initiative || undefined,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [q, status, type, engineer, vesselBx, dealBx, from, to, initiative, start])

  const запрос = useВерификации(фильтры)
  const данные = запрос.data
  const счёт = p73.data?.counts

  const сСброса = <T,>(setter: (v: T) => void) => (v: T) => { setStart(0); setter(v) }

  const снятьСущность = (ключ: string) => {
    const next = new URLSearchParams(sp)
    next.delete(ключ)
    setStart(0)
    setSp(next, { replace: true })
  }

  return (
    <>
      <div className="page-head">
        <h1>Верификации судов</h1>
        <span className="muted">
          {данные ? `найдено: ${данные.total}` : ''}
          {счёт ? ` · всего в ядре: ${счёт.verifications}` : ''}
        </span>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        {/* Семь полей в столбец на телефоне — это весь экран до первой верификации,
            поэтому блок сворачивается; что применено, видно в его заголовке. */}
        <FilterBar id="verifications">
          <div className="f" style={{ flex: 2, minWidth: 220 }}>
            <label htmlFor="vq">Судно (название или IMO)</label>
            <input id="vq" value={q} onChange={(e) => сСброса(setQ)(e.target.value)}
                   placeholder="например: Дон Стар или 8844153" />
          </div>

          <div className="f">
            <label htmlFor="vst">Статус</label>
            <select id="vst" value={status} onChange={(e) => сСброса(setStatus)(e.target.value)}>
              <option value="">любой</option>
              {Object.entries(p73.data?.verification_status ?? {}).map(([к, n]) => (
                <option key={к} value={к}>{названиеСтатуса(к)} ({n})</option>
              ))}
            </select>
          </div>

          <div className="f">
            <label htmlFor="vtp">Тип</label>
            <select id="vtp" value={type} onChange={(e) => сСброса(setType)(e.target.value)}>
              <option value="">любой</option>
              {Object.entries(p73.data?.verification_type ?? {}).map(([к, n]) => (
                <option key={к} value={к}>{ТИПЫ_ВЕРИФИКАЦИИ[к] ?? к} ({n})</option>
              ))}
            </select>
          </div>

          <div className="f" style={{ minWidth: 200 }}>
            <label htmlFor="ven">Инженер</label>
            <select id="ven" value={engineer} onChange={(e) => сСброса(setEngineer)(e.target.value)}
                    disabled={!p73.data}>
              <option value="">любой</option>
              {(p73.data?.engineers ?? []).map((u) => (
                <option key={u.d1_id} value={u.d1_id}>
                  {u.full_name || u.username || `#${u.d1_id}`}{u.is_active ? '' : ' (не работает)'}
                </option>
              ))}
            </select>
          </div>

          <div className="f">
            <label htmlFor="vfrom">Изменена с</label>
            <input id="vfrom" type="date" value={from} onChange={(e) => сСброса(setFrom)(e.target.value)} />
          </div>

          <div className="f">
            <label htmlFor="vto">по</label>
            <input id="vto" type="date" value={to} onChange={(e) => сСброса(setTo)(e.target.value)} />
          </div>

          <div className="f" style={{ minWidth: 150 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 18 }}>
              <input type="checkbox" checked={initiative} style={{ width: 'auto' }}
                     onChange={(e) => сСброса(setInitiative)(e.target.checked)} />
              по инициативе
            </label>
          </div>
        </FilterBar>

        {(vesselBx || dealBx) && (
          <div className="hint" style={{ marginTop: 10 }}>
            Отфильтровано по{' '}
            {vesselBx && (
              <>
                судну <EntityLink kind="vessel" id={vesselBx} resolve />{' '}
                <button onClick={() => снятьСущность('vessel_bx')}>снять</button>{' '}
              </>
            )}
            {dealBx && (
              <>
                сделке <EntityLink kind="deal" id={dealBx} resolve />{' '}
                <button onClick={() => снятьСущность('deal_bx')}>снять</button>
              </>
            )}
          </div>
        )}

        <div className="hint" style={{ marginTop: 10 }}>
          Данные — зеркало портала 73 (обновляется синком). Изменить верификацию можно только
          на портале: этот раздел ничего не пишет.
          {счёт ? ` Кроме этого в ядре лежат ${счёт.auto_verifications} «Верификации-3 Автоматика» —
            это отдельная форма, её раздел не сделан, и здесь их нет.` : ''}
        </div>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <div className="card">
          {/* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12).
              Подписи колонок продублированы в data-label ячеек: шапки там на экране нет. */}
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th className="nowrap">ID</th>
                  <th>Судно</th>
                  <th>Заявка / сделка</th>
                  <th className="nowrap">Инженер</th>
                  <th className="nowrap">Тип</th>
                  <th className="nowrap">Статус</th>
                  <th className="num">Заполнено</th>
                  <th className="num">Фото</th>
                  <th className="nowrap">Изменена</th>
                </tr>
              </thead>
              <tbody>
                {(данные?.rows ?? []).map((v) => <Строка key={v.d1_id} v={v} имяСотрудника={refs.userNameOr} />)}
                {данные && данные.rows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="empty">
                      <div className="empty-title">Под эти фильтры верификаций нет</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {данные && данные.total > РАЗМЕР_СТРАНИЦЫ && (
            <div className="pager">
              <button disabled={start === 0 || запрос.isFetching}
                      onClick={() => setStart(Math.max(0, start - РАЗМЕР_СТРАНИЦЫ))}>← назад</button>
              <span className="muted">
                {start + 1}–{Math.min(start + РАЗМЕР_СТРАНИЦЫ, данные.total)} из {данные.total}
              </span>
              <button disabled={данные.next === null || запрос.isFetching}
                      onClick={() => setStart(данные.next!)}>вперёд →</button>
            </div>
          )}
        </div>
      </QueryState>
    </>
  )
}

function Строка({ v, имяСотрудника }: {
  v: ВерификацияСтрока
  /** refs.userNameOr: имя из справочника ядра, а нет его — имя из учётки портала 73. */
  имяСотрудника: (id?: string | number | null, fallback?: string | null) => string
}) {
  return (
    <tr>
      <td className="mono nowrap" data-label="ID"><Link to={ПУТИ.верификация(v.d1_id)}>{v.d1_id}</Link></td>
      <td data-label="Судно">
        {/* Судно портала = контакт-судно Bitrix; ссылка идёт через мостик bitrix_contact_id.
            Названия пришли вместе со списком — EntityLink их не догружает (никакого N+1). */}
        <EntityLink kind="vessel" id={v.vessel?.bitrix_id} title={v.vessel?.name}
                    empty={v.vessel?.name || '— нет мостика к контакту'} />
        {v.vessel?.imo && <div className="muted mono">IMO {v.vessel.imo}</div>}
      </td>
      {/* Названия сделок длинные («#105433 / Сервис/ тх … / Ежегодные проверки РНО») и в
          восемь колонок не влезают: обрезаем, полное видно во всплывающей подсказке ссылки. */}
      <td data-label="Заявка / сделка"
          style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {v.deal?.bitrix_id
          ? <EntityLink kind="deal" id={v.deal.bitrix_id} title={v.deal.title} />
          : <span className="muted">{v.initiative ? 'по инициативе, без заявки' : 'сделки Bitrix нет'}</span>}
      </td>
      {/* Карточки сотрудника в CRM пока нет — поэтому имя, а не ссылка. Имя берём из
          справочника ядра, а если его нет — из учётки портала. */}
      <td className="nowrap" data-label="Инженер">{имяСотрудника(v.engineer?.bitrix_id, v.engineer?.name)}</td>
      <td className="nowrap" data-label="Тип">{ТИПЫ_ВЕРИФИКАЦИИ[v.type] ?? v.type}</td>
      <td className="nowrap" data-label="Статус"><span className={`badge ${видСтатуса(v.status)}`}>{названиеСтатуса(v.status)}</span></td>
      <td className="num" data-label="Заполнено">{процент(v.fill_pct)}</td>
      {/* Ноль фотографий — это факт, а не «неизвестно»: `photos_total` ядро считает
          запросом и всегда отдаёт числом. Прочерк здесь читался бы как «не знаем», а для
          верификации «фото не приложено ни одного» — прямой повод её не принимать. */}
      <td className="num" data-label="Фото">
        {v.photos_total > 0 ? v.photos_total : <span className="muted" title="фотографий не приложено">0</span>}
      </td>
      <td className="nowrap muted" data-label="Изменена">{fmtDate(v.updated_at || v.created_at, true)}</td>
    </tr>
  )
}

// ═══════════════════════════════ карточка ═══════════════════════════════

function Карточка({ id }: { id: string }) {
  const запрос = useВерификация(id)
  const refs = useRefs()
  const v = запрос.data

  return (
    <>
      <div className="page-head">
        <h1>Верификация #{id}</h1>
        <div className="spacer" />
        {/* Лист для человека, а не документ системы: в Bitrix и на портал он не заливается.
            Фотографии остаются в хранилище портала — лист говорит об этом словами. */}
        <PrintButton готово={!!v} собрать={(св) => листВерификации(v!, { свежесть: св })} />
        <Link to={ПУТИ.верификации}>← к списку</Link>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        {v && (
          <>
            <div className="grid-2">
              <div>
                <div className="card">
                  <h2>Верификация</h2>
                  <dl className="dl">
                    <dt>Судно</dt>
                    <dd>
                      <EntityLink kind="vessel" id={v.vessel?.bitrix_id} title={v.vessel?.name}
                                  empty={v.vessel?.name || '—'} />
                      {v.vessel?.imo ? <span className="muted mono"> · IMO {v.vessel.imo}</span> : null}
                    </dd>
                    <dt>Заявка / сделка</dt>
                    <dd>
                      {v.deal?.bitrix_id
                        ? <EntityLink kind="deal" id={v.deal.bitrix_id} title={v.deal.title} />
                        : <span className="muted">
                            {v.initiative ? 'по инициативе, без заявки' : 'сделки Bitrix нет'}
                          </span>}
                      {v.deal?.location ? <span className="muted"> · {v.deal.location}</span> : null}
                    </dd>
                    <dt>Инженер</dt>
                    <dd>{refs.userNameOr(v.engineer?.bitrix_id, v.engineer?.name)}</dd>
                    <dt>Тип</dt><dd>{ТИПЫ_ВЕРИФИКАЦИИ[v.type] ?? v.type}</dd>
                    <dt>Статус</dt>
                    <dd>
                      <span className={`badge ${видСтатуса(v.status)}`}>{названиеСтатуса(v.status)}</span>
                      {v.reject_reason ? <div className="error" style={{ marginTop: 6 }}>Причина отклонения: {v.reject_reason}</div> : null}
                    </dd>
                    <dt>Заполнено</dt><dd>{процент(v.fill_pct)}</dd>
                    <dt>Создана</dt><dd className="muted">{fmtDate(v.created_at, true)}</dd>
                    <dt>Изменена</dt><dd className="muted">{fmtDate(v.updated_at, true)}</dd>
                    <dt>Отправлена</dt><dd className="muted">{fmtDate(v.submitted_at, true)}</dd>
                    <dt>Проверена</dt>
                    <dd className="muted">
                      {fmtDate(v.reviewed_at, true)}
                      {v.reviewer_name ? ` · ${v.reviewer_name}` : ''}
                    </dd>
                    {/* Баллы и число дефектов портал считает не всегда: баллы появляются
                        только после отправки, defects_found ненулевой
                        у 11 записей из 169 (на 28.07.2026). Пустое поле здесь означает
                        «портал не считал», а не «дефектов нет». */}
                    <dt>Баллы</dt>
                    <dd>
                      {v.points_total === null
                        ? <span className="muted">портал не считал (баллы появляются после отправки)</span>
                        : <>{v.points_total}{v.points_base !== null ? ` (база ${v.points_base})` : ''}</>}
                    </dd>
                    <dt>Дефектов найдено</dt>
                    <dd>
                      {v.defects_found === null
                        ? <span className="muted">портал не заполнил это поле</span>
                        : v.defects_found}
                    </dd>
                  </dl>
                </div>

                <ШапкаСудна header={v.header} />
                <Фото v={v} />
              </div>

              <div>
                <Оговорки v={v} />
                {v.notes && (
                  <div className="card">
                    <h2>Найденные неисправности и предложения</h2>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{v.notes}</div>
                  </div>
                )}
                {v.meta.general_notes && (
                  <div className="card">
                    <h2>Общие замечания</h2>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{v.meta.general_notes}</div>
                  </div>
                )}
              </div>
            </div>

            <ЧекЛистРазделы v={v} />
            <ЧекЛистПункты v={v} />
          </>
        )}
      </QueryState>
    </>
  )
}

function ШапкаСудна({ header }: { header: Record<string, string | null> }) {
  const подписи: Record<string, string> = {
    vessel_name: 'Название', imo: 'IMO', mmsi_callsign: 'MMSI / позывной', flag: 'Флаг',
    port: 'Порт приписки', ship_phone: 'Телефон на судне', ship_email: 'Почта судна',
  }
  const строки = Object.entries(header || {})
  if (!строки.length) return null
  return (
    <div className="card">
      <h2>Шапка формы</h2>
      <dl className="dl">
        {строки.map(([к, з]) => (
          <Fragment key={к}>
            <dt>{подписи[к] ?? к}</dt>
            <dd>{fmtText(з)}</dd>
          </Fragment>
        ))}
      </dl>
      <div className="hint">
        Это то, что инженер вписал в форму на портале, а не карточка судна в CRM. Расхождение
        с карточкой — нормальная находка, а не ошибка показа.
      </div>
    </div>
  )
}

function Оговорки({ v }: { v: ВерификацияКарточка }) {
  const скрытых = Object.keys(v.meta.hidden_groups || {}).length
  const отсутствуют = Object.keys(v.meta.absent_sections || {}).length
  return (
    <div className="card">
      <h2>Что важно знать про эти данные</h2>
      <ul className="plain-list">
        <li>
          Фотографии остаются в хранилище портала 73 — в ядро перенесены только ключи файлов.
          Поэтому ниже видно, сколько их и как они называются, но открыть их здесь нельзя.
        </li>
        {v.photo_count_portal === null && (
          <li>
            Счётчик фотографий в самой записи портала пуст (так у большинства записей), поэтому
            число фотографий посчитано по реестру ядра: <b>{v.photos_total}</b>.
          </li>
        )}
        {отсутствуют > 0 && (
          <li>
            Разделов, отмеченных инженером как «на судне нет»: {отсутствуют}. Они помечены
            ниже — пустые поля в них означают отсутствие оборудования, а не незаполненную форму.
          </li>
        )}
        {скрытых > 0 && (
          <li>
            Групп, скрытых в форме (второй гирокомпас, второй АРБ и подобное): {скрытых}.
            Портал их не показывал инженеру, поэтому они пустые.
          </li>
        )}
        {v.meta.river_register && <li>Судно речного Регистра — форма отличается от морской.</li>}
        {v.meta.full_v3 && <li>Полная верификация версии 3 — состав разделов другой.</li>}
        <li className="muted">
          Порядок полей внутри раздела — по коду поля: jsonb в Postgres не хранит порядок
          ключей исходной формы, восстановить порядок бланка нечем.
        </li>
        {v.meta.unknown_keys.length > 0 && (
          <li className="error">
            В форме есть ключи, которых этот раздел не знает ({v.meta.unknown_keys.join(', ')}) —
            похоже, форму на портале изменили. Их содержимое здесь не показано.
          </li>
        )}
      </ul>
    </div>
  )
}

function ЧекЛистРазделы({ v }: { v: ВерификацияКарточка }) {
  if (!v.sections.length) {
    return <div className="card muted">Чек-лист в записи пуст — на портале форму не заполняли.</div>
  }
  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Чек-лист</h2>
        <div className="spacer" />
        <span className="muted">разделов: {v.sections.length}</span>
      </div>
      {v.sections.map((s) => (
        <div key={s.key} style={{ marginBottom: 18 }}>
          <h3 style={{ marginBottom: 6 }}>
            {s.title}{' '}
            {s.absent
              ? <span className="badge lose">на судне нет</span>
              : <span className="hint" style={{ fontWeight: 'normal' }}>
                  заполнено {s.filled} из {s.fields.length}
                </span>}
          </h3>
          {s.fields.length === 0 ? (
            <div className="empty"><div className="empty-title">Полей в разделе нет.</div></div>
          ) : (
            <div className="table-wrap cards">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '45%' }}>Пункт</th>
                    <th>Значение</th>
                    <th className="nowrap">Поле Bitrix</th>
                  </tr>
                </thead>
                <tbody>
                  {s.fields.map((p) => (
                    <tr key={p.key}>
                      <td data-label="Пункт">
                        {p.label}
                        <div className="muted mono">{p.key}</div>
                      </td>
                      <td data-label="Значение" style={{ whiteSpace: 'pre-wrap' }}>{значениеФормы(p.value)}</td>
                      {/* Код UF показывает, что именно уедет в сделку при утверждении на портале.
                          Пусто — значит поле живёт только в форме и в Bitrix не попадает. */}
                      <td className="mono nowrap muted" data-label="Поле Bitrix">{p.uf || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function ЧекЛистПункты({ v }: { v: ВерификацияКарточка }) {
  if (!v.checklist.length) return null
  return (
    <div className="card">
      <h2>Пункты проверки по приборам</h2>
      {v.checklist.map((б) => (
        <div key={б.key} style={{ marginBottom: 16 }}>
          <h3 style={{ marginBottom: 6 }} className="mono">{б.key}</h3>
          {б.instances.map((э, i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              <div className="hint" style={{ marginBottom: 4 }}>{э.label}</div>
              {/* Подписи пустые намеренно: шапки у этой таблицы нет и на мониторе —
                  пункт, отметка и примечание читаются подряд. Придумывать названия
                  колонок ради карточек значило бы дописать текст, которого на экране нет. */}
              <div className="table-wrap cards">
                <table>
                  <tbody>
                    {э.checks.map((п) => (
                      <tr key={п.key}>
                        <td data-label="">{п.label}</td>
                        <td className="nowrap" data-label="">
                          <span className={`badge ${п.value === 'ok' ? 'won' : п.value === 'no' ? 'lose' : ''}`}>
                            {ОТМЕТКИ[п.value ?? ''] ?? fmtText(п.value)}
                          </span>
                        </td>
                        <td className="muted" data-label="">{п.note || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

function Фото({ v }: { v: ВерификацияКарточка }) {
  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Фотографии</h2>
        <div className="spacer" />
        <span className="muted">{v.photos_total || 0}</span>
      </div>
      {v.photos.length === 0 ? (
        <div className="empty"><div className="empty-title">Фотографий к этой верификации в ядре нет.</div></div>
      ) : (
        <>
          <PhotoGallery вид="verification-photo" файлы={v.photos} />

          {/* Реестр под галереей, а не вместо неё: снимок отвечает на «что сделали»,
              а реестр — на «когда загрузили и сколько весит», и это разные вопросы.
              Ключ в реестре оставлен: по нему запись сходится с порталом 73. */}
          <h3 style={{ marginTop: 20 }}>Реестр файлов</h3>
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Файл</th>
                  <th className="num">Размер</th>
                  <th className="nowrap">Загружен</th>
                </tr>
              </thead>
              <tbody>
                {v.photos.map((ф) => (
                  <tr key={ф.d1_id}>
                    <td data-label="Файл">
                      {fmtText(ф.label)}
                      {/* Сканы Регистра портал помечает маркером формы (form_4.1.1), а не именем файла */}
                      {(ф.label || '').startsWith('form_') && (
                        <span className="hint"> — скан формы Регистра</span>
                      )}
                      <div className="muted mono">{fmtText(ф.r2_key)}</div>
                    </td>
                    <td className="num muted" data-label="Размер">{размерФайла(ф.size_bytes)}</td>
                    <td className="nowrap muted" data-label="Загружен">{fmtDate(ф.created_at, true)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="hint" style={{ marginTop: 8 }}>
            В зеркале хранятся только ключи — сами файлы остаются у портала 73. Ядро читает
            их своим ключом «только чтение» и отдаёт через вход в CRM.
          </div>
        </>
      )}
    </div>
  )
}
